import { Observable } from 'rxjs/Observable'
import { combineEpics } from 'redux-observable'
import { List } from 'immutable'

import { notifyParticipants } from 'utils/NotificationHelper'
import { captureException } from 'utils/SentryHelpers'
import { api } from './api'
import * as MessageActions from 'app/modules/message/actions'
import * as UserSelectors from 'app/modules/user/selectors'
import * as EndUserSelectors from 'app/modules/endUser/selectors'
import * as EndUserActions from 'app/modules/endUser/actions'
import * as ConversationSelectors from 'app/modules/conversation/selectors'
import * as ConversationActions from 'app/modules/conversation/actions'
import * as UserPropertiesSelectors from 'app/modules/user/properties/selectors'
import * as GateSelectors from 'app/modules/gates/selectors'

import { showParticipantsNotification, formatParticipantsNotification } from './utils'
import * as actions from './actions'
import * as selectors from './selectors'

function fetchParticipants(action$, { getState }) {
  return action$.ofType(actions.fetchParticipants.toString())
    .map(action => action.payload)
    .filter(({ conversationId }) => !selectors.participantsLoaded(conversationId)(getState()))
    .switchMap(({ conversationId }) => {

      if (GateSelectors.hasDemoOrg(getState())) {
        return Observable.of(actions.receiveParticipants({ participants: List(), conversationId }))
      } else {
        return api
          .fetch(conversationId)
          .map((participants) => actions.receiveParticipants({ participants, conversationId }))
          .takeUntil(action$.ofType(actions.fetchParticipantsCancelled.toString()))
          .startWith(actions.fetchParticipantsPending({ conversationId }))
          .catch(error => {
            captureException(error)
            return Observable.of(actions.fetchParticipantsFailed({ conversationId, error }))
          })
        }
    })
}

function addParticipants(action$) {
  return action$.ofType(actions.addParticipants.toString())
    .map(action => action.payload)
    .mergeMap(({ conversationId, participants, teams }) => {
      return api
        .add({ conversationId, participants, teams })
        .map(({ participants }) => actions.addParticipantsSuccess({ conversationId, participants }))
        .startWith(actions.addParticipantsPending({ conversationId, participants }))
        .catch(error => {
          captureException(error)
          return Observable.of(actions.addParticipantsFailed({ conversationId, participants, error }))
        })
    })
}

function removeParticipants(action$) {
  return action$
    .ofType(actions.removeParticipants.toString())
    .map(action => action.payload)
    .mergeMap(({ conversationId, participants }) => {
      return api
        .remove(conversationId, participants)
        .map(({ participants }) => actions.removeParticipantsSuccess({ conversationId, participants }))
        .startWith(actions.removeParticipantsPending({ conversationId, participants }))
        .catch(error => {
          captureException(error)
          return Observable.of(actions.removeParticipantsFailed({ conversationId, participants, error }))
        })
    })
}

function conversationEvent(action$, { getState }) {
  const participantEvent$ = action$
    .ofType(MessageActions.receiveMessage.toString())
    .pluck('payload', 'message')
    .filter(({ type }) => type === 'CONVERSATION_EVENT')
    .filter(({ conversationEvent: { type }}) => {
      return type === 'ADDED_PARTICIPANTS' || type === 'REMOVED_PARTICIPANTS'
    })

  const fetchConversationsToNotify$ = participantEvent$
    .filter(event => {
      const state = getState()
      const { authorUserId, currentParticipantIds } = event.conversationEvent
      const currentUser = UserSelectors.currentUser(state)
      const userProperties = UserPropertiesSelectors.properties(state)
      const hasCentralizedRouting = GateSelectors.hasCentralizedRouting(state)
      return showParticipantsNotification(currentUser, authorUserId, currentParticipantIds, userProperties, hasCentralizedRouting)
    })
    .map(({ conversationId }) => ConversationActions.fetchConversation({ conversationId }))


  const participantUpdate$ = participantEvent$
    .filter(({ conversationId }) => selectors.currentConversationId(getState()) === conversationId)
    .map(({ conversationEvent: { type, participantIds }, conversationId }) => {
      switch (type) {
        case 'ADDED_PARTICIPANTS':
          return actions.addParticipantsPending({ participants: participantIds, conversationId })
        case 'REMOVED_PARTICIPANTS':
          return actions.removeParticipantsPending({ participants: participantIds, conversationId })
      }
    })

  return Observable.merge(fetchConversationsToNotify$, participantUpdate$)
}


function fetchCurrentParticipants(action$) {
  return action$.ofType(ConversationActions.conversationChanged.toString())
    .pluck('payload', 'conversationId')
    .filter(conversationId => !!conversationId)
    .map(conversationId => actions.fetchParticipants({ conversationId }))
}

export const epic = combineEpics(fetchParticipants, addParticipants, removeParticipants, conversationEvent, fetchCurrentParticipants)



// WEBPACK FOOTER //
// ./src/scripts/app/modules/participants/epic.js